import Assert from "tbAssert";

import uiRouterModule from "@uirouter/angularjs";
import oneLoginOidcModule from "tbClient/shims/oneLoginOidc";

import AuthConstants from "./AuthConstants";
import TbAppConfigDataServiceModule from "tbClient/config/tb-app-config/TbAppConfigDataService";

const mStateSvc = new WeakMap();
const mOneLoginSvc = new WeakMap();
const mTimeoutSvc = new WeakMap();
const mLogSvc = new WeakMap();
const mTbAppConfigDataSvc = new WeakMap();
const mCachedUserProfile = new  WeakMap();
const mTokenRenewalTimeout = new WeakMap();

class AuthService {
    constructor($state, oneLoginOidc, $timeout, $log, TbAppConfigDataService) {
        Assert.StrictAssert($state != null, "Invalid or missing $state service.");
        Assert.StrictAssert(oneLoginOidc != null, "Invalid or missing OneLogin OIDC service");
        Assert.StrictAssert($timeout != null, "Invalid or missing timeout service");
        Assert.StrictAssert($log != null, "Invalid or missing log service");
        Assert.StrictAssert(TbAppConfigDataService != null, "Invalid or missing app config data service");

        mStateSvc.set(this, $state);
        mOneLoginSvc.set(this, oneLoginOidc);
        mTimeoutSvc.set(this, $timeout);
        mLogSvc.set(this, $log);
        mTbAppConfigDataSvc.set(this, TbAppConfigDataService);
        mCachedUserProfile.set(this, null);

        mTokenRenewalTimeout.set(this, null);
    }

    //noinspection JSMethodCanBeStatic
    get _storageSvc() {
        return localStorage;
    }

    get _signoutUrl() {
        return mTbAppConfigDataSvc.get(this).tbLogoutUrl;
    }

    get _cachedProfile() {
        return mCachedUserProfile.get(this);
    }

    set _cachedProfile(updatedProfile) {
        if (updatedProfile != null) {
            mCachedUserProfile.set(this, updatedProfile);
        }
        else {
            mCachedUserProfile.set(this, null);
        }
    }

    login() {
        mOneLoginSvc.get(this).authorize();
    }

    handleAuthentication() {
        const logSvc = mLogSvc.get(this);

        return new Promise((resolve, reject) => {
            mOneLoginSvc.get(this).parseHash((err, authResult) => {
                    if (err) {
                        reject(err);
                    }
                    else {
                        resolve(authResult);
                    }
                });
            })
            .then((authResult) => {
                    if (authResult && authResult.accessToken && authResult.idToken) {
                        this.setSession(authResult);
                        logSvc.debug("Handle Authentication: Retrieved and stored auth session tokens.");
                    }
                    else {
                        logSvc.debug("Handle Authentication: No Result, Completed Without Error.");
                    }

                    // Return the authResult so the controller can access the tokens
                    return authResult;
                })
                .catch((err) => {
                    if (err) {
                        logSvc.error("Error handling authentication", err);
                        throw err;
                    }
                    else {
                        logSvc.error("Error handling authentication, but no details provided.");
                        throw new Error("Error handling authentication, but no details provided.");
                    }
                });
    }

    setSession(authResult) {
        if (!authResult) {
            throw new Error("Cannot set authentication session from null authentication result");
        }

        const expiresAt = JSON.stringify((authResult.expiresIn * 1000) + new Date().getTime());
        this._storageSvc.setItem(
            AuthConstants.storageKeys.accessTokenKey,
            authResult.accessToken
        );

        this._storageSvc.setItem(
            AuthConstants.storageKeys.idTokenKey,
            authResult.idToken
        );

        this._storageSvc.setItem(
            AuthConstants.storageKeys.expiresAtKey,
            expiresAt
        );

        this.scheduleRenewal();
    }

    renewToken() {
        const oneLoginSvc = mOneLoginSvc.get(this);

        return new Promise((resolve, reject) => {
                oneLoginSvc.checkSession({}, (err, result) => {
                    if (err) {
                        reject(err);
                    }
                    else {
                        resolve(result);
                    }
                });
            })
            .then((result) => {
                this.setSession(result);
            })
            .catch((err) => {
                mLogSvc.get(this).error("Error renewing session", err);
                throw err;
            });
    }

    scheduleRenewal() {
        const expiresAt = JSON.parse(this._storageSvc.getItem(AuthConstants.storageKeys.expiresAtKey));
        const renewalDelay = expiresAt - Date.now();
        if (renewalDelay > 0) {
            const tokenRenewalTimeout = mTimeoutSvc.get(this)(() => {
                this.renewToken();
            }, renewalDelay);

            mTokenRenewalTimeout.set(this, tokenRenewalTimeout);
        }
    }

    logout() {
        const logSvc = mLogSvc.get(this);

        // Clear cached profile and cancel token renewal
        this._cachedProfile = null;

        const tokenRenewalTimeout = mTokenRenewalTimeout.get(this);
        if (tokenRenewalTimeout) {
            mTimeoutSvc.get(this).cancel(tokenRenewalTimeout);
            mTokenRenewalTimeout.set(this, null);
        }

        logSvc.debug("Logging out from OneLogin...");

        // Log out from OneLogin SSO provider FIRST (before clearing tokens)
        // The OneLogin logout needs the id_token to build the logout URL
        const oneLoginSvc = mOneLoginSvc.get(this);
        if (oneLoginSvc && oneLoginSvc.logout) {
            // Note: oneLoginSvc.logout() will clear tokens itself and redirect
            // So the code below won't execute - the page will navigate away
            oneLoginSvc.logout();
        } else {
            // If OneLogin logout isn't available, clear tokens manually and redirect
            this._storageSvc.removeItem(AuthConstants.storageKeys.accessTokenKey);
            this._storageSvc.removeItem(AuthConstants.storageKeys.idTokenKey);
            this._storageSvc.removeItem(AuthConstants.storageKeys.expiresAtKey);

            const appUrl = mTbAppConfigDataSvc.get(this).tbClientAppUrl;
            window.location.assign(appUrl);
        }

    }

    // Security and other notes:
    // Because this reads the expireAt value from local storage, the user can change it at will.
    // Therefore, this check only says that this client *believes* that the user is authenticated,
    // and that any api calls made with a stored access token will succeed.
    // However, because the api will check the access token on its own
    // (and verify that the token has not been tampered with), the api call may fail *even* if this check
    // returns true.
    isAuthenticated() {
        //Check whether current time is past the access token's expiration time
        const expiresAt = JSON.parse(this._storageSvc.getItem(AuthConstants.storageKeys.expiresAtKey));

        // Note that if there is no stored expiration time, then expiresAt will be null.
        // In relational comparisons (but not equality ones), null gets coerced to 0, and so,
        // this comparison will return false (assuming JS Date 0 is midnight, 1/1/1970)
        return new Date().getTime() < expiresAt;
    }

    getProfile() {
        const cachedProfile = this._cachedProfile;
        if (cachedProfile != null) {
            return Promise.resolve(cachedProfile);
        }

        return this.loadProfile();
    }

    loadProfile() {
        return new Promise((resolve, reject) => {
            const accessToken = this._storageSvc.getItem(AuthConstants.storageKeys.accessTokenKey);
            if (!accessToken) {
                reject(new Error("Access Token must exist to fetch profile"));
            }

            mOneLoginSvc.get(this).client.userInfo(accessToken, (err, profile) => {
                if (profile != null) {
                    this._cachedProfile = profile;
                    resolve(profile);
                    return;
                }

                reject(err);
            });
        });
    }

    static factory(...factoryArgs) {
        return new AuthService(...factoryArgs);
    }
}

AuthService.$inject = [
    "$state",
    "oneLoginOidc",
    "$timeout",
    "$log",
    "TbAppConfigDataService"
];

AuthService.dependencies = [
    uiRouterModule,
    oneLoginOidcModule,
    TbAppConfigDataServiceModule
];

export default AuthService;



//////////////////
// WEBPACK FOOTER
// ./src/services/auth/AuthService.js
// module id = 1Ref
// module chunks = 1